import { FC, ReactNode, useContext } from 'react';
import { Box, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { SelectedShopProvider } from 'src/contexts/SelectedShop';
import { SidebarContext } from 'src/contexts/SidebarContext';
import Sidebar from './Sidebar';
import Header from './Header';
import { NavBadgesProvider } from './NavBadgesContext';
import { Auth } from 'src/Auth';
import { brand } from 'src/theme/brand';

/** Width of the icon-only desktop sidebar. */
export const COLLAPSED_SIDEBAR_WIDTH = '76px';

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const { collapsed } = useContext(SidebarContext);

  return (
    <Auth>
      <SelectedShopProvider>
        <NavBadgesProvider>
        <Box
          sx={{
            // Header and content offsets follow the sidebar width.
            '--sidebar-w': collapsed ? COLLAPSED_SIDEBAR_WIDTH : theme.sidebar.width,
            flex: 1,
            height: '100%',
            minHeight: '100vh',
            backgroundColor: brand.canvas,
            '.MuiPageTitle-wrapper': {
              background: 'transparent',
              marginBottom: theme.spacing(1.5),
              borderBottom: 'none',
              boxShadow: 'none',
              px: 0
            }
          }}
        >
          <Header />
          <Sidebar />
          <Box
            sx={{
              position: 'relative',
              zIndex: 5,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              pt: `${theme.header.height}`,
              minHeight: '100%',
              transition: 'margin-left .2s ease',
              [theme.breakpoints.up('lg')]: {
                ml: 'var(--sidebar-w)'
              }
            }}
          >
            <Box
              component="main"
              sx={{
                flex: 1,
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                px: { xs: 1.5, sm: 2, md: 3 },
                pt: { xs: 1, sm: 1.5 },
                pb: { xs: 2, sm: 2.5 },
                // Page-level containers use the full content area; the shell already provides the gutters.
                '& > .MuiContainer-root, & .MuiContainer-maxWidthLg, & .MuiContainer-maxWidthXl': {
                  maxWidth: 'none',
                  px: 0
                }
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
        </NavBadgesProvider>
      </SelectedShopProvider>
    </Auth>
  );
};

export default SidebarLayout;
